import React, { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import type { RunningHourCounterDto } from '../../core/pms/_models'
import type { EquipmentDto } from '../../core/_models'
import { createRunningHourCounter } from '../../core/pms/_requests'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  visible: boolean
  onClose: () => void
  vesselId?: number
  vesselName?: string
  equipmentOptions: EquipmentDto[]
  onSuccess: (counter: RunningHourCounterDto) => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const CreateCounterModal: FC<Props> = ({
  visible,
  onClose,
  vesselId,
  vesselName,
  equipmentOptions,
  onSuccess,
}) => {
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  const selectedEquipment = useMemo(() => {
    const id = Number(selectedEquipmentId)
    if (!id) return undefined
    return equipmentOptions.find(e => e.id === id)
  }, [equipmentOptions, selectedEquipmentId])

  const counterName = useMemo(() => {
    if (!selectedEquipment) return ''
    return selectedEquipment.name || selectedEquipment.code || 'Running Hour Counter'
  }, [selectedEquipment])

  useEffect(() => {
    if (!visible) return
    if (equipmentOptions.length === 1) {
      setSelectedEquipmentId(String(equipmentOptions[0].id))
    } else if (!equipmentOptions.find(e => String(e.id) === selectedEquipmentId)) {
      setSelectedEquipmentId('')
    }
  }, [visible, equipmentOptions, selectedEquipmentId])

  if (!visible) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!vesselId) {
      toast.error('Select a vessel first', { position: 'top-center' })
      return
    }
    const equipmentId = Number(selectedEquipmentId)
    if (!equipmentId) {
      toast.error('Select an equipment', { position: 'top-center' })
      return
    }
    if (!counterName) {
      toast.error('Counter name could not be determined', { position: 'top-center' })
      return
    }
    const payload: RunningHourCounterDto = {
      vesselId,
      equipmentId,
      name: counterName,
      currentValue: 0,
    }
    setSubmitting(true)
    try {
      const created = await createRunningHourCounter(payload)
      toast.success('Counter created successfully', { position: 'top-center' })
      onSuccess(created)
      onClose()
    } catch (error: any) {
      toast.error('Failed to create counter', { position: 'top-center' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '520px', maxWidth: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>Add Running Hour Counter</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className='flex-grow-1 overflow-auto px-4 py-3'>
            <div className='mb-4'>
              <label className={LABEL}>Vessel</label>
              <input
                type='text'
                className='form-control text-dark'
                value={vesselName || 'Selected Vessel'}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            <div className='mb-4'>
              <label className={LABEL}>
                Equipment <span className='text-danger'>*</span>
              </label>
              {equipmentOptions.length === 0 ? (
                <div className='text-muted fs-7'>
                  All equipment already have counters for this vessel.
                </div>
              ) : (
                <SearchableSelect
                  className='form-select text-dark'
                  value={selectedEquipmentId}
                  onChange={(e) => setSelectedEquipmentId(e.target.value)}
                >
                  <option value=''>Select equipment</option>
                  {equipmentOptions.map(eq => (
                    <option key={eq.id} value={String(eq.id)}>
                      {eq.name} {eq.code ? `(${eq.code})` : ''}
                    </option>
                  ))}
                </SearchableSelect>
              )}
            </div>
            <div className='mb-4'>
              <label className={LABEL}>Counter Name</label>
              <input
                type='text'
                className='form-control text-dark'
                value={counterName || '-'}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
          </div>
          <div className='border-top bg-white px-4 py-3 d-flex justify-content-end gap-2'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={submitting || !vesselId || equipmentOptions.length === 0}
            >
              {submitting ? 'Creating...' : 'Create Counter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
